import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { Box, Heading, Modal, Stack, Tiles } from "@seneca/senekit";

import { regions } from "common/static/regions";
import { regionMetas } from "common/static/regions/regionMetas";

import { isUserManaged } from "seneca-common/features/user/state";
import { useRedirectOnCondition } from "seneca-common/utils/hooks/routing";

import { ModalPortal } from "features/common/modals/components/ModalNodeContext";
import WithSetModalOpenOnMount from "features/common/modals/components/WithSetModalOpenOnMount";
import { CHOOSE_REGION_PATH } from "features/common/routing/consts";
import { getRegion } from "features/regions/selectors";
import { setRegionPreference } from "features/user";

import RegionOption from "./RegionOption/RegionOption";

type Props = {
  closeModal?: () => void;
};

export default function RegionSelectionModal({
  closeModal: closeModalProp
}: Props) {
  const history = useHistory();
  const selectedRegion = useSelector(getRegion);
  const dispatch = useDispatch();
  const location = useLocation();
  const userIsManaged = useSelector(isUserManaged);

  useRedirectOnCondition(
    userIsManaged,
    location.pathname.replace(CHOOSE_REGION_PATH, "")
  );

  const closeModal = closeModalProp
    ? closeModalProp
    : () => {
        const { pathname } = location;
        const newLocation = pathname.replace(CHOOSE_REGION_PATH, "");

        history.push(newLocation);
      };

  const onRegionSelection = async (region: any) => {
    await dispatch(setRegionPreference(region));

    closeModal();

    if (closeModalProp) {
      // if we're using state to render component refresh on the page we're on
      window.location && window.location.reload();
    } else {
      // if we're using routes we need to "refresh" on the previous location as
      // window.location.reload() leaves us on the route that the component is rendered on, even if we call goBack first
      const currentPathname = location.pathname;
      const replaced = currentPathname.replace(CHOOSE_REGION_PATH, "");
      window.location && window.location.replace(replaced);
    }
  };

  return (
    <ModalPortal>
      <WithSetModalOpenOnMount>
        <Modal onCloseClick={closeModal} fullscreenOnMobile>
          <Stack space="xlarge">
            <Heading level="3" align="center">
              Choose your region
            </Heading>
            <Box paddingX={["xlarge", "xxxlarge"]} paddingY="xlarge">
              <Tiles columns={[1, 2]} space={["large", "xlarge"]}>
                {Object.keys(regions).map((region, i) => {
                  return (
                    regions[region].visible && (
                      <RegionOption
                        selected={selectedRegion === region}
                        key={`${region}` + i}
                        clickHandler={() => onRegionSelection(region)}
                        name={regions[region].localName}
                        flagImg={
                          (regionMetas as any)[region] &&
                          (regionMetas as any)[region].flag
                        }
                      />
                    )
                  );
                })}
              </Tiles>
            </Box>
          </Stack>
        </Modal>
      </WithSetModalOpenOnMount>
    </ModalPortal>
  );
}
